import React, { useContext } from "react";
import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_ROLES_AND_ACCESS,
  PAGE_ROUTE_USER_GUIDE,
  useKivunovaTranslation,
  PAGE_ROUTE_SOPS
} from "@kivunova/kivufrontendcommon";
import PublicInfoCallout from "./PublicInfoCallout";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { MANAGE_COMPANY_SOP_SECTIONS } from "./constants/manageCompanySopContent";
import { MANAGE_COMPANY_SOP_IMAGES } from "./constants/manageCompanySopImages";
import type { ManageCompanySopImageConfig } from "./types";
import {
  publicInfoBodySx,
  publicInfoGuideSectionSx,
  publicInfoLinkSx,
  publicInfoOrderedListSx,
  publicInfoSectionTitleWithImageSx,
  userGuideSectionImageSx
} from "./constants/publicInfoPageStyles";

const ManageCompanySopImage: React.FC<{
  image: ManageCompanySopImageConfig;
  testId: string;
}> = ({ image, testId }) => {
  const { t } = useKivunovaTranslation();

  return (
    <Box
      component="img"
      src={image.src}
      alt={t(image.altKey, image.altFallback)}
      data-testid={testId}
      sx={userGuideSectionImageSx}
    />
  );
};

const ManageCompanySopPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const runBooksPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_SOPS, language, defaultLang)
      : `/${PAGE_ROUTE_SOPS}`;
  const rolesPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_ROLES_AND_ACCESS, language, defaultLang)
      : `/${PAGE_ROUTE_ROLES_AND_ACCESS}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="manage-company-sop-page"
        eyebrow={t("manage-company-sop-page-eyebrow", "Run books")}
        title={t("manage-company-sop-page-title", "How to manage your company")}
        intro={t(
          "manage-company-sop-page-intro",
          "A company admin guide for managing your team, business details, and subscription plan."
        )}
      />

      <PublicInfoCallout testId="manage-company-sop-page-note">
        {t(
          "manage-company-sop-page-note",
          "Company admin only. If you do not see the Company menu or these tabs, ask your administrator to check your role."
        )}
      </PublicInfoCallout>

      <Stack spacing={4} sx={{ mt: 3 }}>
        {MANAGE_COMPANY_SOP_SECTIONS.map((section, sectionIndex) => (
          <Box
            key={section.id}
            component="section"
            sx={publicInfoGuideSectionSx}
            data-testid={`manage-company-sop-section-${section.id}`}
          >
            <Typography component="h2" sx={publicInfoSectionTitleWithImageSx}>
              {sectionIndex + 1}. {t(section.titleKey, section.titleFallback)}
            </Typography>
            <Box component="ol" sx={publicInfoOrderedListSx}>
              {section.steps.map((step, stepIndex) => {
                const image =
                  step.imageId != null
                    ? MANAGE_COMPANY_SOP_IMAGES[step.imageId]
                    : undefined;

                return (
                  <Box key={`${section.id}-${stepIndex}`} component="li">
                    <Typography
                      component="span"
                      variant="body1"
                      sx={publicInfoBodySx}
                    >
                      {t(step.bodyKey, step.bodyFallback)}
                    </Typography>
                    {image ? (
                      <ManageCompanySopImage
                        image={image}
                        testId={`manage-company-sop-image-${step.imageId}`}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      <PublicInfoCtaPanel
        title={t(
          "manage-company-sop-page-cta-title",
          "Need more help with roles?"
        )}
        testId="manage-company-sop-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "manage-company-sop-page-cta-body",
            "See what each role can do before you assign employees, or read the full setup guide."
          )}{" "}
          <MuiLink
            component={Link}
            to={runBooksPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-company-sop-page-cta-all-guides-link", "Run books")}
          </MuiLink>
          {t("manage-company-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={rolesPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-company-sop-page-cta-roles-link", "Roles & access")}
          </MuiLink>
          {t("manage-company-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-company-sop-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("manage-company-sop-page-cta-mid", " · ")}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("manage-company-sop-page-cta-contact-link", "Contact us")}
          </MuiLink>
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default ManageCompanySopPage;
